import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { routesData, heroData } from "../data/mock";
import {
  ArrowRight,
  Clock,
  MapPin,
  MessageCircle,
  Navigation,
  TrendingUp,
} from "lucide-react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";

const RoutesSection = () => {
  const navigate = useNavigate();

  const handleCardClick = (routeId) => {
    if (!routeId) return;
    navigate(`/routes/${routeId}`);
  };

  const handleBookRoute = (from, to, e) => {
    e.stopPropagation();
    const message = encodeURIComponent(
      `Hello Bahubali Cabs! I want to book a taxi from *${from}* to *${to}*. Please share the itinerary and best fare.`,
    );
    window.open(`https://wa.me/${heroData.whatsapp}?text=${message}`, "_blank");
  };

  return (
    <section
      id="routes"
      className="py-24 bg-[#0a0f1a] relative overflow-hidden"
    >
      {/* 🔹 Background Architecture */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{ backgroundImage: `url("data:image/svg+xml,...")` }}
      />

      {/* Glow Orbs */}
      <div className="absolute top-1/4 -left-20 w-96 h-96 bg-amber-600/10 rounded-full blur-[120px]" />
      <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-amber-500/10 rounded-full blur-[120px]" />

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/20 rounded-full mb-6"
          >
            <TrendingUp className="w-4 h-4 text-amber-500" />
            <span className="text-amber-400 text-xs font-black uppercase tracking-widest">
              Most Traveled Destinations
            </span>
          </motion.div>

          <h2 className="text-4xl md:text-6xl font-black text-white tracking-tighter mb-6 uppercase italic">
            Outstation <span className="text-amber-500">Taxi</span> Service
          </h2>
          <p className="text-slate-400 text-lg md:text-xl max-w-2xl mx-auto font-medium">
            Explore Rajasthan with our expertly curated routes. Fixed fares,
            verified drivers.
          </p>
        </div>

        {/* 🔹 Grid with Interactive Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
          {routesData.map((route, index) => (
            <motion.div
              key={route.id}
              initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card
                onClick={() => handleCardClick(route.id)}
                className="group relative h-[400px] overflow-hidden rounded-[2.5rem] border-none shadow-2xl transition-all duration-500 cursor-pointer"
              >
                {/* Image Layer */}
                <div className="absolute inset-0">
                  <img
                    src={route.cardImage || "/images/default-route.jpg"}
                    alt={`${route.from} to ${route.to}`}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1a] via-[#0a0f1a]/60 to-transparent" />
                </div>

                {/* Content Overlay */}
                <div className="absolute inset-0 p-8 flex flex-col justify-end">
                  {/* Route Badge */}
                  <div className="flex gap-3 mb-4">
                    <span className="bg-white/10 backdrop-blur-md px-3 py-1 rounded-lg text-[10px] font-bold text-white uppercase border border-white/10 flex items-center gap-1">
                      <Clock className="w-3 h-3 text-amber-500" />{" "}
                      {route.duration || "Flexible"}
                    </span>
                    <span className="bg-white/10 backdrop-blur-md px-3 py-1 rounded-lg text-[10px] font-bold text-white uppercase border border-white/10 flex items-center gap-1">
                      <Navigation className="w-3 h-3 text-amber-500" />{" "}
                      {route.distance || "Verified"}
                    </span>
                  </div>

                  <h3 className="text-3xl font-black text-white mb-6 flex items-center gap-3 italic">
                    {route.from}{" "}
                    <ArrowRight className="w-6 h-6 text-amber-500" /> {route.to}
                  </h3>

                  <div className="flex flex-col sm:flex-row gap-4 items-center">
                    <Button
                      onClick={(e) => handleBookRoute(route.from, route.to, e)}
                      className="w-full sm:w-auto bg-amber-500 hover:bg-white text-slate-950 font-black px-8 py-6 rounded-2xl transition-all shadow-xl shadow-amber-500/20"
                    >
                      <MessageCircle className="w-5 h-5 mr-2" />
                      BOOK ON WHATSAPP
                    </Button>
                    <span className="text-white/50 text-xs font-bold uppercase tracking-widest group-hover:text-white transition-colors">
                      View Itinerary →
                    </span>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* 🔹 Trust Seal Footer */}
        <div className="mt-20 py-8 border-t border-white/5 text-center">
          <p className="text-slate-500 text-sm font-bold uppercase tracking-[0.3em] mb-4">
            Authorized Rajasthan Tourism Partner
          </p>
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-4 text-slate-400 text-xs font-medium">
            {[
              "Mount Abu",
              "Jaipur",
              "Jodhpur",
              "Ahmedabad",
              "Nathdwara",
              "Kumbhalgarh",
            ].map((city) => (
              <span
                key={city}
                className="hover:text-amber-500 transition-colors cursor-default"
              >
                #{city}Taxi
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default RoutesSection;
